import * as THREE from 'three';
import { settings } from '@/config';
import type { TickState } from '@/types';
import type { StoneInteraction } from './StoneInteraction';

/**
 * CURSOR-FOLLOW GLOW PLANE (2026-07-20, user: "wherever I hover on the stones I want a glow
 * effect — not that emission one — a plane mesh that follows the cursor, with a glow texture
 * that only appears when I hover the stones. Very subtle, a spread glow that reflects on the
 * stones").
 *
 * The trick, exactly as the user framed it: one small quad carrying a soft radial glow texture
 * (canvas-generated), additive-blended, that glides along the stone surface under the cursor.
 * Its purpose is to SMOOTH THE EMISSION'S EDGES — "it does not affect our stone back texture;
 * it is just to make a smooth glow of the emission texture" — so it rides the emission, it
 * doesn't replace it:
 *
 *  - Follows {@link StoneInteraction.hover}, and ONLY over stones with an emissive twin.
 *  - Its strength is COUPLED to the hovered twin's live emission intensity: no emission,
 *    no glow — the resting baked texture is never brightened.
 *  - AXIS LOCKED: always camera-facing (billboard), never oriented per hit face — per-face
 *    rotation is exactly what flickered when the cursor crossed brick edges.
 *  - Position eases, opacity eases — the light glides rather than teleporting.
 *  - `depthWrite:false` + additive: it can never occlude anything, only brighten it.
 *  - Excluded from the cloud depth prepass and the bloom pass (overlay, not scene geometry).
 */
export class CursorGlow {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly texture: THREE.CanvasTexture;

  /** Smoothed pose + visibility (0..1) the tick eases toward the raycast hit. */
  private readonly pos = new THREE.Vector3();
  private fade = 0;
  private hasPose = false;

  private readonly _toCam = new THREE.Vector3();

  constructor(
    private readonly interaction: StoneInteraction,
    private readonly camera: THREE.Camera,
  ) {
    const cfg = settings.stones.cursorGlow;

    this.texture = makeGlowTexture();
    this.material = new THREE.MeshBasicMaterial({
      map: this.texture,
      color: new THREE.Color(cfg.color),
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false, // brightens what's behind it, never occludes
      toneMapped: false,
      side: THREE.DoubleSide,
      // STENCIL MASK — the reason this can never touch the baked stone: the emission overlays
      // (renderOrder 2, drawn first) stamp ref 1 where their geometry passes the depth test,
      // and this quad (renderOrder 3) only passes where that stamp equals 1. Three enables the
      // stencil test only when stencilWrite is true, so it's set with a ZERO write mask:
      // test-only, writes nothing.
      stencilWrite: true,
      stencilWriteMask: 0,
      stencilRef: 1,
      stencilFunc: THREE.EqualStencilFunc,
    });

    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.material);
    this.mesh.name = 'cursor-glow';
    this.mesh.visible = false;
    this.mesh.renderOrder = 3; // over the baked stones and the emission overlays
    // Never on the bloom layer and never a raycast target — it must not glow-amplify itself
    // via the bloom pass, and it must not block the very hover ray that drives it.
    this.mesh.raycast = () => undefined;
  }

  update(tick: TickState): void {
    const cfg = settings.stones.cursorGlow;
    const hover = this.interaction.hover;
    const active = cfg.enabled && this.interaction.enabled && hover.active;

    // Ease visibility toward on/off; ease pose toward the hit. Frame-rate independent damps.
    const dt = Math.min(tick.delta, 1 / 30);
    const fadeK = 1 - Math.exp(-dt / Math.max(cfg.fadeSec, 1e-3));
    const followK = 1 - Math.exp(-dt / Math.max(cfg.followSec, 1e-3));

    this.fade += ((active ? 1 : 0) - this.fade) * fadeK;

    if (active) {
      if (!this.hasPose) {
        // First contact: snap, don't glide in from wherever the plane last was.
        this.pos.copy(hover.point);
        this.hasPose = true;
      } else {
        this.pos.lerp(hover.point, followK);
      }
    } else if (this.fade < 0.01) {
      this.hasPose = false; // fully faded out — next hover starts fresh
    }

    // EMISSION COUPLING (2026-07-20): the glow exists to smooth the EMISSION's edges, so its
    // strength rides the hovered twin's live glow intensity — zero emission ⇒ zero glow, and
    // the resting baked texture is never brightened by it.
    const emission = cfg.emissionCoupled ? this.interaction.hoverEmissionIntensity() : 1;

    const visible = this.fade > 0.01;
    this.mesh.visible = visible && (!cfg.emissionCoupled || emission > 0.005);
    if (!this.mesh.visible) return;

    this.material.opacity = cfg.opacity * this.fade * emission;
    this.material.color.set(cfg.color);
    this.mesh.scale.setScalar(cfg.size);

    // AXIS LOCKED (2026-07-20, user: "that glow thing is not rotating — lock the axis, it only
    // should be visible in the front"). No per-face orientation: crossing a brick edge used to
    // snap the quad to the new face's normal, which read as flicker. The quad now always faces
    // the CAMERA (billboard — copy its orientation verbatim, zero per-face variation) and lifts
    // off the surface TOWARD the camera, which never z-fights regardless of which face was hit.
    this.mesh.quaternion.copy((this.camera as THREE.PerspectiveCamera).quaternion);
    this._toCam.copy((this.camera as THREE.PerspectiveCamera).position).sub(this.pos).normalize();
    this.mesh.position.copy(this.pos).addScaledVector(this._toCam, cfg.offset);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}

/** Soft radial glow tile: bright core falling off smoothly to fully transparent edges — the
 *  "spread" is in the gradient, so the quad's edges are invisible even against dark stone. */
function makeGlowTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0.0, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.45)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.12)');
  g.addColorStop(1.0, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
